import type { ApiErrorBody, ErrorCode } from '@shiocast/contracts';

const BASE = '/api/v2';

/**
 * A failed API call, carrying the stable code the contract defines. Callers branch on
 * `code`, never on the message, so a wording change is never a behaviour change.
 */
export class ApiRequestError extends Error {
  constructor(
    readonly code: ErrorCode | 'NETWORK_ERROR',
    message: string,
    readonly status: number,
    readonly requestId?: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

let accessToken: string | null = null;
let currentStation: string | null = null;
let onUnauthenticated: (() => void) | null = null;

/**
 * The access token is held in memory only. It is deliberately not in localStorage:
 * anything that can run script in the panel could read it there, and a token that
 * survives a reload is a token an attacker can exfiltrate at leisure. The refresh
 * token lives in an HttpOnly cookie the browser will not hand to script at all.
 */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * The station every call is for.
 *
 * An operator with one station never sets this and the API picks their only one. An
 * operator with several picks, and every request from then on says which, so nothing
 * depends on a server-side notion of where they are.
 */
export function setCurrentStation(stationId: string | null): void {
  currentStation = stationId;
}

export function hasAccessToken(): boolean {
  return accessToken !== null;
}

/**
 * The token, for the one caller that cannot send a header: a browser cannot set headers
 * on a WebSocket handshake, so the realtime socket carries it in the query string. Kept
 * to that single use — everything over HTTP sends it as a header.
 */
export function getAccessToken(): string | null {
  return accessToken;
}

/** The station every call is for, for the realtime socket's own filtering. */
export function getCurrentStation(): string | null {
  return currentStation;
}

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Send `body` as-is under this content type instead of as JSON (an opaque document). */
  rawContentType?: string;
  /** Set for the refresh call itself, so a failed refresh does not recurse. */
  skipRefresh?: boolean;
  signal?: AbortSignal;
}

let refreshInFlight: Promise<boolean> | null = null;

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  // One transparent refresh attempt. Concurrent callers share it, so a page that fires
  // six requests on load does not rotate the refresh token six times and trip the
  // reuse detection.
  if (response.status === 401 && !options.skipRefresh) {
    const refreshed = await refreshOnce();
    if (refreshed) {
      const retry = await send(path, options);
      return handle<T>(retry);
    }
    onUnauthenticated?.();
  }

  return handle<T>(response);
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) {
    headers['content-type'] = options.rawContentType ?? 'application/json';
  }
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  // Which station the operator is working in. Sent on every call rather than threaded
  // through each one, because the answer is the same for all of them and forgetting it
  // on one would quietly send that request to a different station.
  if (currentStation) headers['x-station'] = currentStation;
  const body =
    options.body === undefined
      ? undefined
      : options.rawContentType
        ? (options.body as BodyInit)
        : JSON.stringify(options.body);

  try {
    return await fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      // The refresh and device cookies are HttpOnly and scoped to the auth path.
      credentials: 'include',
      ...(body === undefined ? {} : { body }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    throw new ApiRequestError(
      'NETWORK_ERROR',
      error instanceof Error ? error.message : 'The panel could not reach the API.',
      0,
    );
  }
}

async function handle<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // A body that is not JSON on an error response is still an error; the status and
    // the request id are what the operator needs.
  }

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    throw new ApiRequestError(
      body?.error?.code ?? 'INTERNAL_ERROR',
      body?.error?.message ?? `Request failed with status ${response.status}`,
      response.status,
      body?.error?.request_id ?? response.headers.get('x-request-id') ?? undefined,
      body?.error?.details,
    );
  }

  return payload as T;
}

async function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await send('/auth/refresh', { method: 'POST', body: {}, skipRefresh: true });
      if (!response.ok) return false;
      const body = (await response.json()) as { access_token?: string };
      if (!body.access_token) return false;
      accessToken = body.access_token;
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so callers that arrived during the refresh still see
      // this promise rather than starting a second one.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

/**
 * Stream a file body to the API with upload progress.
 *
 * fetch() cannot report upload progress, so this one path uses XMLHttpRequest. The
 * server reads the raw stream; nothing here buffers the file beyond what the browser
 * does itself, which is what lets a two-hour episode upload from the panel.
 */
export function uploadRaw(
  path: string,
  file: Blob,
  onProgress: (fraction: number) => void,
  options: { contentType?: string } = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `${BASE}${path}`);
    if (accessToken) xhr.setRequestHeader('authorization', `Bearer ${accessToken}`);
    xhr.setRequestHeader(
      'content-type',
      options.contentType ?? (file.type || 'application/octet-stream'),
    );
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else
        reject(new ApiRequestError('NETWORK_ERROR', `Upload failed (${xhr.status})`, xhr.status));
    };
    xhr.onerror = () =>
      reject(new ApiRequestError('NETWORK_ERROR', 'Upload failed: network error', 0));
    xhr.send(file);
  });
}

/**
 * Download an authenticated file (a CSV export, say) through the browser.
 *
 * A plain link cannot carry the bearer token, so the file is fetched and handed to the
 * browser as a blob. Sized for exports, not for media.
 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const response = await fetch(`${BASE}${path}`, {
    headers: {
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...(currentStation ? { 'x-station': currentStation } : {}),
    },
  });
  if (!response.ok) {
    throw new ApiRequestError(
      'NETWORK_ERROR',
      `Download failed (${response.status})`,
      response.status,
    );
  }
  const url = URL.createObjectURL(await response.blob());
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}
